import { useState, useEffect } from 'react';
import OTPInput from 'otp-input-react';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import useToast from '../../../util/hooks/useToast';
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';

import Button from '../../../components/Button';
import { encryptData } from '../../../util/helpers/encrypt';
import formatSeconds from '../../../util/helpers/formatSeconds';
import ArrowRight from '../../../assets/svg_components/ArrowRight';
import { setRefetchGetUser } from '../../../redux/slices/user/profileSlice';
import { ReactComponent as ResendCodeIcon } from '../../../assets/images/resend-code-icon.svg';
import AuthHeader from '../../../components/AuthBackground/_partials/AuthHeader';
import { VERIFY_EMAIL_OTP, RESEND_EMAIL_OTP, CREATE_USER_CANADA } from '../../../graphql/mutation';

import VerifyEmailStyles from './VerifyEmailScreen.module.scss';

const VerifyEmailModal = () => {
  const Toast = useToast();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [verifyEmailOtp] = useMutation(VERIFY_EMAIL_OTP);
  const [resendEmailOtp] = useMutation(RESEND_EMAIL_OTP);
  const [createUserCanada] = useMutation(CREATE_USER_CANADA);
  const { firstName, lastName, email, password, referralCode } = useAppSelector(
    (state) => state.user
  );

  const [pin, setPin] = useState('');
  const [time, setTime] = useState(120);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);

  useEffect(() => {
    if (error) {
      Toast.error(error);
    }
  }, [error, setError]);

  useEffect(() => {
    let id: any;

    if (email) {
      id = setInterval(() => {
        setTime((prevTime) => prevTime - 1);
      }, 1000);
    } else if (!email && !isSuccess) {
      navigate(-1);
    }

    return () => clearInterval(id);
  }, [email]);

  const handleResendOtp = async () => {
    try {
      setError('');
      if (!sendingOtp) {
        setSendingOtp(true);
        const resp = await resendEmailOtp({
          variables: { email }
        });
        if (resp?.data?.resendEmailOtp?.status === 200) {
          Toast.success(resp?.data?.resendEmailOtp?.message);
        }

        if (resp?.data?.resendEmailOtp?.status === 400) {
          setError(resp?.data?.resendEmailOtp?.message);
          return;
        }
        setSendingOtp(false);
        setTime(120);
      }
    } catch (err) {
      setIsLoading(false);
      setSendingOtp(false);
      const e = err as any;
      if (e && e.graphQLErrors && e.graphQLErrors.length > 0) {
        setError(e.graphQLErrors[0].message);
      } else {
        setError('Something went wrong');
      }
    }
  };

  const handleCreateUser = async () => {
    try {
      setIsLoading(true);
      setError('');
      const request = {
        email,
        password,
        firstName,
        lastName,
        referralCode,
        deviceId: 'web',
        code: ''
      };

      const reqPayload = await encryptData(JSON.stringify(request));
      const resp = await createUserCanada({
        variables: {
          data: reqPayload
        }
      });

      if (resp?.data?.createUserCanada?.status === 200) {
        setIsSuccess(true);
        setPin('');
        setIsLoading(false);
        localStorage.setItem('token', resp?.data?.createUserCanada?.token);
      }

      if (resp?.data?.createUserCanada?.status === 400) {
        setError(resp?.data?.createUserCanada?.message);
        setIsLoading(false);
      }
    } catch (err) {
      setIsLoading(false);
      const e = err as any;
      if (e && e?.graphQLErrors && e?.graphQLErrors?.length > 0) {
        setError(e?.graphQLErrors[0]?.message);
      } else {
        setError('Something went wrong');
      }
    }
  };

  const handleEmailVerify = async () => {
    try {
      setIsLoading(true);
      setError('');
      const request = {
        otp: pin,
        email
      };

      const resp = await verifyEmailOtp({
        variables: request
      });

      if (resp?.data?.verifyMobileEmailOTP?.status === 200) {
        handleCreateUser();
        setIsLoading(false);
      }

      if (resp?.data?.verifyMobileEmailOTP.status === 400) {
        setError(resp?.data?.verifyMobileEmailOTP?.message);
        setIsLoading(false);
      }
    } catch (err) {
      setIsLoading(false);
      const e = err as any;
      if (e && e?.graphQLErrors && e?.graphQLError?.length > 0) {
        setError(e?.graphQLErrors[0]?.message);
      } else {
        setError('Something went wrong');
      }
    }
  };

  return (
    <div className={VerifyEmailStyles.verifyEmail__wrapper}>
      <AuthHeader className={VerifyEmailStyles.verifyEmail__authHeader} />
      <div className={VerifyEmailStyles.verifyEmail__contentWrapper}>
        {!isSuccess && (
          <div className={VerifyEmailStyles.verifyEmail__content}>
            <h4 className={VerifyEmailStyles.verifyEmail__headText}>Verify Email Address</h4>

            <p className={VerifyEmailStyles.verifyEmail__subtext}>
              We have sent an email to{' '}
              <strong className={VerifyEmailStyles.verifyEmail__subtextIdentity}>{email}</strong>{' '}
              containing a verification code.
            </p>

            <div className={VerifyEmailStyles.verifyEmail__pin}>
              <OTPInput
                className={VerifyEmailStyles.verifyEmail__pinWrapper}
                inputClassName={VerifyEmailStyles.verifyEmail__pinInput}
                value={pin}
                onChange={setPin}
                autoFocus={false}
                OTPLength={4}
                otpType="number"
                disabled={false}
                secure
              />
            </div>

            <div className={VerifyEmailStyles.verifyEmail__instruction}>
              <div className={VerifyEmailStyles.verifyEmail__notice}>
                <p className={VerifyEmailStyles.verifyEmail__noticeText}>
                  Having trouble finding your OTP? Check your Spam/Promotions folder. No OTP still?
                  Click on &quot;Resend&quot; button.
                </p>
              </div>
            </div>

            <Button
              className={VerifyEmailStyles.verifyEmail__button}
              title="Verify email address"
              disabled={pin.length < 4 || isLoading}
              onPress={handleEmailVerify}
              isLoading={isLoading}
            />

            <div className={VerifyEmailStyles.verifyEmail__resendBeforeTimeout}>
              {time > 0 && (
                <>
                  <div className={VerifyEmailStyles.verifyEmail__redirect}>
                    <div className={VerifyEmailStyles.verifyEmail__redirectText}>
                      <ArrowRight
                        className={VerifyEmailStyles.verifyEmail__redirectArrow}
                        stroke="#5941A9"
                      />
                      <span
                        className={VerifyEmailStyles.verifyEmail__redirectClickable}
                        onClick={() => {
                          navigate('/get-started');
                        }}>
                        Go back to create account
                      </span>
                    </div>
                  </div>
                  <p className={VerifyEmailStyles.verifyEmail__beforeTimeoutText}>
                    <ResendCodeIcon />
                    Resend code in :{' '}
                    <span className={VerifyEmailStyles.verifyEmail__resendTimer}>
                      {formatSeconds(time)}
                    </span>
                  </p>
                </>
              )}
            </div>
            {time < 0 && (
              <>
                <div className={VerifyEmailStyles.verifyEmail__resend}>
                  <p className={VerifyEmailStyles.verifyEmail__resendText}>
                    <span
                      className={`${VerifyEmailStyles.verifyEmail__resendTimer} ${VerifyEmailStyles.verifyEmail__resendClickable}`}
                      onClick={handleResendOtp}>
                      Resend code
                    </span>
                  </p>
                </div>

                <div className={VerifyEmailStyles.verifyEmail__redirect}>
                  <div className={VerifyEmailStyles.verifyEmail__redirectText}>
                    <ArrowRight
                      className={VerifyEmailStyles.verifyEmail__redirectArrow}
                      stroke="#5941A9"
                    />
                    <span
                      className={VerifyEmailStyles.verifyEmail__redirectClickable}
                      onClick={() => {
                        navigate('/get-started');
                      }}>
                      Go back to create account
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {isSuccess && (
          <div className={VerifyEmailStyles.verifyEmail__content}>
            <h1 className={VerifyEmailStyles.verifyEmail__successHeadText}>
              Account Created Successfully
            </h1>

            <p className={VerifyEmailStyles.verifyEmail__successSubtext}>
              You have successfully created an account!
            </p>

            <Button
              onPress={() => {
                dispatch(setRefetchGetUser(true));
                navigate('/onboarding/service-selection');
              }}
              className={VerifyEmailStyles.verifyEmail__successButton}
              title="Continue to dashboard"
            />
          </div>
        )}
      </div>
    </div>
  );
};
export default VerifyEmailModal;
